'use client'

/**
 * MotionTier2Mount — client-side lazy mounter for Tier 2 motion effects.
 * T6, Phase D of SiteSmith Builder Rethink (2026-05-19).
 *
 * Reads the `<script id="motion-tier2-effects">` JSON inlined by the
 * route page, dynamic-imports each effect module, locates section
 * elements via `data-motion-effect-id`, and attaches the effect.
 *
 * Save-data + slow-connection short-circuit: if `navigator.connection`
 * reports `saveData === true` or `effectiveType ∈ {slow-2g, 2g}`,
 * this component returns immediately. The Tier 1 short-circuit (low-
 * bandwidth attr on <html>) is handled by motion.css.
 *
 * Tier 2 effects are 1-3KB gz each plus a single shared Anime.js core
 * load (≤10KB gz). The dynamic import groups per-effect into separate
 * chunks via Next.js's webpack split-chunks.
 */
import { useEffect } from 'react'

type EffectHandle = {
  play(): void
  seek(progress: number): void
  destroy(): void
}

type EffectModule = {
  default: (target: Element, params?: Record<string, unknown>) => EffectHandle
}

const MODULE_LOADERS: Record<string, () => Promise<EffectModule>> = {
  // Each entry maps a registry effect_id → its dynamic import.
  // Keeping this map explicit (instead of `import(\`./effects/${id}\`)`)
  // lets webpack code-split each effect into its own chunk.
  morph_brand_mark: () =>
    import('./effects/morph-brand-mark') as unknown as Promise<EffectModule>,
  kinetic_text_reveal: () =>
    import('./effects/kinetic-text-reveal') as unknown as Promise<EffectModule>,
  parallax_layered: () =>
    import('./effects/parallax-layered') as unknown as Promise<EffectModule>,
}

function isLowBandwidth(): boolean {
  if (typeof navigator === 'undefined') return false
  // navigator.connection is non-standard; treat absence as fast.
  const conn = (navigator as unknown as { connection?: { saveData?: boolean; effectiveType?: string } }).connection
  if (!conn) return false
  if (conn.saveData) return true
  if (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') return true
  return false
}

export function MotionTier2Mount() {
  useEffect(() => {
    if (isLowBandwidth()) return
    const scriptEl = document.getElementById('motion-tier2-effects')
    if (!scriptEl || !scriptEl.textContent) return
    let effectIds: string[]
    try {
      const parsed = JSON.parse(scriptEl.textContent)
      effectIds = Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
    } catch {
      return
    }
    if (effectIds.length === 0) return

    const handles: EffectHandle[] = []
    let cancelled = false

    ;(async () => {
      for (const id of effectIds) {
        const loader = MODULE_LOADERS[id]
        if (!loader) continue
        try {
          const mod = await loader()
          if (cancelled) return
          const targets = document.querySelectorAll(`[data-motion-effect-id*="${id}"]`)
          targets.forEach((t) => {
            try {
              const h = mod.default(t)
              h.play()
              handles.push(h)
            } catch {
              // per-target failure is non-fatal
            }
          })
        } catch {
          // module load failure is non-fatal; section still renders.
        }
      }
    })()

    return () => {
      cancelled = true
      for (const h of handles) {
        try { h.destroy() } catch { /* noop */ }
      }
    }
  }, [])

  return null
}
